import * as React from "react";
import { Link, Stack, type SxProps, type Theme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useLegalPublished } from "@/api/legal";
import { legalDocLabel, legalPath, type LegalDoc } from "@/features/legal/docs";

/** A quiet row of links to the legal documents. ``newTab`` opens them
 *  in a new tab so an in-app reader keeps their place. */
export function LegalLinks({
  docs,
  newTab = false,
  sx,
}: {
  docs: readonly LegalDoc[];
  newTab?: boolean;
  sx?: SxProps<Theme>;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Stack
      component="nav"
      aria-label={t("legal.nav_label")}
      direction="row"
      sx={[{ gap: 2, flexWrap: "wrap" }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {docs.map((doc) => (
        <Link
          key={doc}
          href={legalPath(doc)}
          variant="caption"
          color="text.secondary"
          underline="hover"
          {...(newTab ? { target: "_blank", rel: "noopener" } : {})}
        >
          {legalDocLabel(t, doc)}
        </Link>
      ))}
    </Stack>
  );
}

/** :func:`LegalLinks` where the deployment publishes the documents
 *  (``useLegalPublished``), nothing elsewhere: for the places a person
 *  sees on any deployment (sign-in, Help). */
export function PublishedLegalLinks(
  props: React.ComponentProps<typeof LegalLinks>,
): React.JSX.Element | null {
  const published = useLegalPublished();
  return published ? <LegalLinks {...props} /> : null;
}
